import * as d3 from 'd3';
import Moment from 'moment';

export class D3Chart {
  constructor(element, data, yAxisTitle = 'Energy Usage', reportUnit) {
    // load in arguments from config object
    this.data = data;
    this.element = element;
    this.yAxisTitle = yAxisTitle;
    this.reportUnit = reportUnit;
    window.addEventListener('resize', (e) => this.draw(e));
    // create the chart
    this.draw();
  }

  draw() {
    // Define width
    this.width = this.element.offsetWidth;

    // Set height to min value of 450
    this.height = this.width < 390 ? 450 : 490;
    this.margin = {
      top: 20,
      bottom: 75,
      left: this.width > 862 ? 100 : 65,
      right: this.width > 862 ? 60 : 30,
    };

    this.svgHeight = this.height - (this.margin.top + this.margin.bottom);
    this.svgWidth = this.width <= 0 ? 0 : this.width - (this.margin.left + this.margin.right);

    // set up parent element and SVG
    this.element.innerHTML = '';
    const svg = d3.select(this.element).append('svg');
    svg.attr('width', this.width);
    svg.attr('height', this.height);
    svg.attr('id', 'line-graph');

    // we'll actually be appending to a <g> element
    this.plot = svg.append('g').attr('transform', `translate(${this.margin.left},${this.margin.top})`);
    if (this.data.length > 0) {
      this.createScales();
      this.addAxes();
      this.addGrid();
      this.addLines();
      this.addCircle('actual', 3, '#2F4858');
      this.addTooltip(this.xScale, this.yScale, this.data, this.yAxisTitle, this.reportUnit);
      this.addAnimation();
    }
  }

  update(data, unit, reportUnit) {
    this.data = data;
    this.yAxisTitle = unit;
    this.reportUnit = reportUnit;
    // draw the rest of the graph
    this.draw();
  }

  createScales() {
    // shorthand to save typing later
    const m = this.margin;

    // calculate max and min for data
    const xExtent = d3.extent(this.data, (d) => d[0]);

    // make y-axis bigger than max value
    const yExtent = d3.extent(this.data, (d) => Math.max(d[1], d[2], d[3]) * 1.1);

    // force zero baseline if all data points are positive
    if (yExtent[0] > 0) {
      yExtent[0] = 0;
    }

    this.xScale = d3
      .scaleTime()
      .range([0, this.svgWidth])
      .domain(xExtent);

    this.yScale = d3
      .scaleLinear()
      .range([this.height - (m.top + m.bottom), 0])
      .domain(yExtent);
  }

  addAxes() {
    const m = this.margin;

    // set date, default to %e = padded day of the month
    // let dateUnit = '%e';
    // if (this.reportUnit === 'hour') {
    //   dateUnit = '%H';
    // } else if (this.reportUnit === 'month') {
    //   dateUnit = '%m';
    // }
    // create and append axis elements
    const xAxis = d3.axisBottom().scale(this.xScale);

    if (this.width < 700 && this.reportUnit === 'month') {
      xAxis.ticks(d3.timeMonth.every(2)).tickFormat(d3.timeFormat('%b'));
    } else if (this.width < 700 && this.reportUnit === 'day') {
      xAxis.ticks(6);
    } else if (this.width < 700 && this.reportUnit === 'hour') {
      xAxis.ticks(4);
    }

    const startDate = Moment(this.data[0][0]).format('MMM DD');
    const endDate = Moment(this.data[this.data.length - 1][0]).format('MMM DD');
    const xAxisTitle = `${startDate} - ${endDate}`;

    const yAxis = d3
      .axisLeft()
      .scale(this.yScale)
      .ticks(10, '~s');

    this.plot
      .append('g')
      .style('font-size', '10px')
      .attr('class', 'x axis')
      .attr('transform', `translate(0, ${this.height - (m.top + m.bottom)})`)
      .call(xAxis)
      .selectAll('text')
      .attr('y', 22)
      .attr('x', -28)
      .attr('dy', '.25em')
      .attr('transform', 'rotate(-25)')
      .style('text-anchor', 'start');

    this.plot
      .append('text')
      .attr('class', 'axisTitle')
      .attr('text-anchor', 'middle') // this makes it easy to centre the text as the transform is applied to the anchor
      .attr(
        'transform',
        `translate(${this.svgWidth * 0.5}, ${this.height - (this.margin.top + this.margin.bottom / 3)})`,
      )
      // centre below axis
      .text(xAxisTitle);

    this.plot
      .append('g')
      .style('font-size', '11px')
      .attr('class', 'y axis')
      .call(yAxis)
      .select('.domain')
      .remove()
      .selectAll('.tick text')
      .attr('x', 4)
      .attr('dy', -4);

    this.plot
      .append('text')
      .attr('class', 'axisTitle')
      // this makes it easy to centre the text as the transform is applied to the anchor
      .attr('text-anchor', 'middle')
      // text is drawn off the screen top left, move down and out and rotate
      .attr('transform', `translate(${-this.margin.left / 1.5},${this.svgHeight / 2})rotate(-90)`)
      .text(`${this.yAxisTitle} Usage`);

    if (this.width < 700) {
      this.plot.style('font-size', '13px');
    }
  }

  addGrid() {
    const yGrid = d3
      .axisLeft()
      .scale(this.yScale)
      .tickSize(-this.svgWidth)
      .tickFormat('')
      .ticks(6);

    // add the Y gridlines
    this.plot
      .append('g')
      .attr('class', 'grid')
      .call(yGrid)
      .select('.domain')
      .remove();
  }

  addLines() {
    const baseline = d3
      .line()
      .defined((d) => d[1] !== null)
      .x((d) => this.xScale(d[0]))
      .y((d) => this.yScale(d[1]));

    const targetline = d3
      .line()
      .defined((d) => d[2] !== null)
      .x((d) => this.xScale(d[0]))
      .y((d) => this.yScale(d[2]));

    const actualline = d3
      .line()
      .defined((d) => d[3] !== null)
      .x((d) => this.xScale(d[0]))
      .y((d) => this.yScale(d[3]));

    this.addLine(baseline, '#86BBD8', ['stroke-dasharray', '5, 6']);
    // this.addCircle('baseline', 1, '#86BBD8');
    this.addLine(targetline, '#F6AE2D', ['stroke-dasharray', '5, 6']);
    // this.addCircle('target', 2, '#F6AE2D');
    this.addLine(actualline, '#2F4858');
  }

  addLine(line, color, style = []) {
    this.plot
      .append('path')
      // use data stored in `this`
      .datum(this.data)
      .classed('line', true)
      .attr('d', line)
      // set stroke to specified color, or default to red
      .style('stroke', color || 'ffffff')
      .style(style[0], style[1]);
  }

  addCircle(type, value, color) {
    this.plot
      .append('g')
      .attr('class', `${type} circles`)
      .selectAll('circle')
      .data(this.data)
      .enter()
      .append('circle')
      // use data stored in `this`
      .classed('circle', true)
      .attr('r', 0)
      .attr('cx', (d) => this.xScale(d[0]))
      .attr('cy', (d) => this.yScale(d[value]))
      .style('stroke', (d) => (d[value] >= 0 ? color : 'transparent'))
      .style('fill', (d) => (d[value] >= 0 ? 'white' : 'transparent'));
  }

  addTooltip(xScale, yScale, data, unit, reportUnit) {
    const focus = this.plot
      .append('g')
      .attr('id', 'focus')
      .attr('class', 'focus')
      .style('display', 'none');

    focus
      .append('line')
      .attr('class', 'x-hover-line hover-line')
      .attr('y1', 0)
      .attr('y2', this.svgHeight);

    focus.append('circle').attr('class', 'circle--baseline');

    focus.append('circle').attr('class', 'circle--target');

    focus.append('circle').attr('class', 'circle--actual');

    const tooltip = d3
      .select('.line-graph')
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0);

    function bisectDate(dateArray, mouseDate) {
      return d3.bisectLeft(dateArray, mouseDate);
    }

    function findYValueFromDate(selectedDate) {
      for (let i = 0; i < data.length; i += 1) {
        if (data[i][0] === selectedDate) {
          return data[i];
        }
      }
      return false;
    }

    function moveTooltip(d, index, nodes, touch = '') {
      d3.select('#focus').style('display', null);
      let mousePositionX = 0;
      let mousePositionY = 0;
      let bisectedDate;
      let tooltipDate;
      let mousePosition = [0, 0];
      const lineGraph = document.getElementById('line-graph');
      const { width } = lineGraph.getBoundingClientRect();

      if (touch) {
        [mousePosition] = d3.touches(nodes[index]);
        [mousePositionX, mousePositionY] = mousePosition;
      } else {
        [mousePositionX, mousePositionY] = d3.mouse(nodes[index]);
      }
      const date = xScale.invert(mousePositionX);
      const arrayColumn = (arr, n) => arr.map((x) => x[n]);
      const dateArray = arrayColumn(data, 0);
      const i = bisectDate(arrayColumn(data, 0), date);

      if (i === 0) {
        [bisectedDate] = dateArray;
      } else {
        const d0 = dateArray[i - 1];
        const d1 = dateArray[i];
        bisectedDate = date - d0 > d1 - date ? d1 : d0;
      }

      switch (reportUnit.toString()) {
        case 'month':
          tooltipDate = Moment(bisectedDate).format('MMMM');
          break;
        case 'day':
          tooltipDate = Moment(bisectedDate).format('MMMM Do');
          break;
        case 'hour':
          tooltipDate = Moment(bisectedDate).format('MMMM Do hA');
          break;
        default:
          break;
      }

      const yValue = findYValueFromDate(bisectedDate);
      const tooltipHTML = `
        <div style="font-weight: 600; margin-bottom: 1rem;">${tooltipDate}</div>
        <div class="tooltip__item">
          <div class="color-box" style="background-color: #86BBD8;"></div>
          <div class="tooltip__text--baseline">${yValue[1].toFixed(3)} ${unit}</div>
        </div>
        <div class="tooltip__item">
          <div class="color-box" style="background-color: #F6AE2D;"></div>
          <div class="tooltip__text--target">${yValue[2].toFixed(3)} ${unit}</div>
        </div>
        <div class="tooltip__item">
          <div class="color-box" style="background-color: #2f4858;"></div>
          <div class="tooltip__text--actual">${yValue[3].toFixed(3)} ${unit}</div>
        </div>`;

      d3.select('.line-graph')
        .select('.tooltip')
        .style('opacity', 0.9)
        .style('display', 'block')
        .html(tooltipHTML);

      if (window.innerWidth < 599) {
        mousePositionY = 170;
      }

      if (touch) {
        if (mousePositionX < 0) {
          mousePositionX = 0;
        } else if (mousePositionX > width - 90) {
          mousePositionX = width - 90;
        }
        if (mousePositionX > width - 210) {
          if (window.innerWidth > window.innerHeight) {
            if (window.innerWidth < 599) {
              d3.select('.line-graph')
                .select('.tooltip')
                .style('left', `${mousePositionX - 80}px`)
                .style('top', `${mousePositionY - 30}px`);
            } else {
              d3.select('.line-graph')
                .select('.tooltip')
                .style('left', `${mousePositionX + 105}px`)
                .style('top', `${mousePositionY - 30}px`);
            }
          } else {
            d3.select('.line-graph')
              .select('.tooltip')
              .style('left', `${mousePositionX - 80}px`)
              .style('top', `${mousePositionY - 30}px`);
          }
        } else if (mousePositionX <= width - 210) {
          if (window.innerWidth > window.innerHeight) {
            if (window.innerWidth < 599) {
              d3.select('.line-graph')
                .select('.tooltip')
                .style('left', `${mousePositionX + 70}px`)
                .style('top', `${mousePositionY - 30}px`);
            } else {
              d3.select('.line-graph')
                .select('.tooltip')
                .style('left', `${mousePositionX + 270}px`)
                .style('top', `${mousePositionY - 30}px`);
            }
          } else {
            d3.select('.line-graph')
              .select('.tooltip')
              .style('left', `${mousePositionX + 80}px`)
              .style('top', `${mousePositionY - 30}px`);
          }
        }
      } else if (!touch) {
        if (mousePositionX > width - 220) {
          d3.select('.line-graph')
            .select('.tooltip')
            .style('left', `${d3.event.pageX - 165}px`)
            .style('top', `${mousePositionY - 30}px`);
        } else {
          d3.select('.line-graph')
            .select('.tooltip')
            .style('left', `${d3.event.pageX + 15}px`)
            .style('top', `${mousePositionY - 30}px`);
        }
      }

      d3.select('#focus').attr('transform', `translate(${xScale(bisectedDate)}, 0)`);
      d3.select('#focus')
        .select('.circle--baseline')
        .attr('cy', `${yScale(yValue[1])}`);
      d3.select('#focus')
        .select('.circle--target')
        .attr('cy', `${yScale(yValue[2])}`);
      d3.select('#focus')
        .select('.circle--actual')
        .attr('cy', `${yScale(yValue[3])}`);
    }

    this.plot
      .append('rect')
      .attr('id', 'overlay')
      .attr('class', 'overlay')
      .attr('width', this.svgWidth)
      .attr('height', this.svgHeight)
      .on('mouseover', () => {
        focus.style('display', null);
      })
      .on('mouseout touchend', () => {
        focus.style('display', 'none');
        tooltip.style('display', 'none');
      })
      .on('mousemove', (d, index, nodes) => {
        moveTooltip(d, index, nodes);
      })
      .on('touchmove', (d, index, nodes) => {
        moveTooltip(d, index, nodes, 'touch');
      });
  }

  addAnimation() {
    // const totalLength = this.plot.select('path.line').node().getTotalLength();
    // this.plot.select('path.line')
    //       .attr("stroke-dasharray", totalLength + " " + totalLength)
    //       .attr("stroke-dashoffset", totalLength)
    //       .transition()
    //         .duration(1800)
    //         .attr("stroke-dashoffset", 0);

    this.plot
      .selectAll('circle')
      .transition()
      .attr('r', 3)
      .delay((d, i) => i * 35);
  }
}
